import {
  first_truthy,
  first_value,
  normalize_text,
  object_field,
  path_value,
  to_array,
  to_number,
} from './view_case_details_core_helpers';

const DOCUMENT_NAME_FIELDS = ['file_name', 'document_name', 'original_file_name'];
const DOCUMENT_URL_FIELDS = ['file_url', 'document_url', 'presigned_url', 'preview_url', 'url', 'original_url'];
const DOCUMENT_QUESTION_TOKENS = ['file', 'upload', 'attachment', 'document', 'evidence'];
const WARN_TONE_TOKENS = ['warn', 'disputed', 'declined'];
const QUESTION_TYPE_PATHS = [
  ['question_type', 'question_type'],
  ['question_type', 'type'],
  ['question_type'],
  ['type'],
  ['input_type'],
  ['field_type'],
];
const RAW_ANSWER_PATHS = [['answer'], ['answer_text'], ['value']];
export const NO_ANSWER_PROVIDED = 'No Answer Provided';

function field_value(record = {}, field = '') {
  return object_field(record, field);
}
function first_field_value(record = {}, fields = []) {
  return first_value(fields.map((field) => field_value(record, field)));
}
function first_path_value(record = {}, paths = []) {
  return first_value(paths.map((path) => path_value(record, path)));
}
function first_defined_path_value(record = {}, paths = []) {
  return paths.map((path) => path_value(record, path)).find((value) => value != null);
}
export function question_type_descriptor(question = {}) {
  return normalize_text(first_path_value(question, QUESTION_TYPE_PATHS));
}
export function is_document_like_object(value = {}) {
  return Boolean(first_field_value(value, DOCUMENT_NAME_FIELDS.concat(DOCUMENT_URL_FIELDS)));
}
export function is_document_question(question = {}) {
  const descriptor = question_type_descriptor(question).toLowerCase();
  return DOCUMENT_QUESTION_TOKENS.some((token) => descriptor.includes(token));
}

function raw_answer_value(question = {}) {
  return first_defined_path_value(question, RAW_ANSWER_PATHS);
}

function is_object(value) {
  return typeof value === 'object' && value !== null;
}

function is_document_answer_item(item) {
  return is_object(item) && is_document_like_object(item);
}

export function is_document_answer(question = {}) {
  const raw_answer = raw_answer_value(question);
  if (is_document_question(question)) return true;
  return Array.isArray(raw_answer)
    ? raw_answer.some(is_document_answer_item)
    : is_document_answer_item(raw_answer);
}

function object_answer_value(raw_answer = {}) {
  return normalize_text(first_path_value(raw_answer, [['answer_text'], ['value']]));
}

function array_answer_value(raw_answer = []) {
  return raw_answer.map(normalize_text).filter(Boolean).join(', ');
}

export function answer_value(question = {}) {
  const raw_answer = raw_answer_value(question);
  return answer_value_readers()
    .find((reader) => reader.matches(raw_answer))
    .read(raw_answer);
}

function answer_value_readers() {
  return [
    { matches: Array.isArray, read: array_answer_value },
    { matches: is_object, read: object_answer_value },
    { matches: () => true, read: normalize_text },
  ];
}

function normalise_answer_array(raw_answer = []) {
  return raw_answer.map(normalize_text).filter(Boolean);
}

function normalise_answer_string(raw_answer = '') {
  return raw_answer.split(',').map(normalize_text).filter(Boolean);
}

function normalise_single_answer(question = {}) {
  const single_value = answer_value(question);
  return single_value ? [single_value] : [];
}

export function answer_values(question = {}) {
  const raw_answer = raw_answer_value(question);
  if (Array.isArray(raw_answer)) return normalise_answer_array(raw_answer);
  return typeof raw_answer === 'string'
    ? normalise_answer_string(raw_answer)
    : normalise_single_answer(question);
}

function raw_option_values(question = {}) {
  return first_value([
    question?.multi_select_choices,
    question?.choices,
    question?.options,
    question?.question?.multi_select_choices,
    question?.question?.choices,
    question?.question?.options,
  ]);
}

function normalise_option(option) {
  return normalize_text(option?.choice || option?.label || option?.value || option);
}

export function question_option_values(question = {}) {
  return to_array(raw_option_values(question)).map(normalise_option).filter(Boolean);
}

export function section_title(section = {}, fallback_index = 0) {
  return normalize_text(first_value([
    section?.section_name,
    section?.name,
    section?.main_section_name,
    section?.title,
    `Facts ${fallback_index + 1}`,
  ]));
}

export function fact_tone(question = {}, value = '') {
  if (value === NO_ANSWER_PROVIDED) return 'neutral';
  const source = [question?.tone, question?.status, value].join(' ').toLowerCase();
  if (WARN_TONE_TOKENS.some((token) => source.includes(token))) return 'warn';
  return source.includes('strong') ? 'strong' : 'ok';
}

function question_fact_label(question = {}) {
  return normalize_text(first_value([
    question?.question_text,
    question?.label,
    question?.name,
    'Answer',
  ]));
}

function question_fact_context(context = {}) {
  return {
    application_form_id: to_number(context?.application_form_id),
    form_id: to_number(context?.form_id),
    section_title: normalize_text(context?.section_title),
  };
}

export function normalize_question_fact(question = {}, context = {}) {
  const value = answer_value(question) || NO_ANSWER_PROVIDED;
  const builders = [
    { matches: () => true, build: () => build_question_fact(question, value, answer_values(question), question_fact_context(context)) },
  ];
  return builders.find((builder) => builder.matches()).build();
}

function build_question_fact(question, value, values, fact_context) {
  return {
    ...fact_context,
    label: question_fact_label(question),
    question_description: normalize_text(question?.question_description),
    value,
    tone: fact_tone(question, value),
    values,
    question_type: question_type_descriptor(question),
    options: question_option_values(question),
    is_mandatory: Boolean(question?.is_mandatory),
    section_question_id: to_number(question?.section_question_id),
    question_id: to_number(question?.question_id || question?.id),
  };
}

function main_section_sub_sections(main_section = {}) {
  return Array.isArray(main_section?.sub_sections) ? main_section.sub_sections : [main_section];
}

function application_form_source(application_form = {}) {
  return application_form?.form || application_form || {};
}

export function form_sections(application_form = {}) {
  const form = application_form_source(application_form);
  if (Array.isArray(form?.sections)) return form.sections;
  return Array.isArray(form?.main_sections) ? form.main_sections.flatMap(main_section_sub_sections) : [];
}

export function form_questions(application_form = {}) {
  const form = application_form?.form || application_form || {};
  const section_questions = form_sections(application_form)
    .flatMap((section) => to_array(section?.questions));
  const direct_questions = to_array(form?.questions);
  return [...section_questions, ...direct_questions];
}

export function submitted_answer_records(application_form = {}) {
  const form = application_form?.form || {};
  return [
    ...to_array(application_form?.answers),
    ...to_array(application_form?.submitted_answers),
    ...to_array(application_form?.application_answers),
    ...to_array(application_form?.form_answers),
    ...to_array(application_form?.responses),
    ...to_array(form?.answers),
    ...to_array(form?.submitted_answers),
    ...to_array(form?.responses),
  ];
}

function question_label_value(question = {}) {
  return first_truthy([
    question?.question_text,
    question?.question?.question_text,
    question?.question?.text,
    question?.question?.label,
    question?.question?.name,
    question?.label,
    question?.name,
    question?.text,
  ]);
}

export function question_label(question = {}) {
  return normalize_text(question_label_value(question));
}

function label_includes(question = {}, phrases = []) {
  const label = question_label(question).toLowerCase();
  return phrases.some((phrase) => label.includes(phrase));
}

export function is_submitter_question(question = {}) {
  return label_includes(question, [
    'submitted by',
    'submitter',
    'requester name',
    'requestor name',
    'applicant name',
    'your name',
    'full name',
    'whistleblower',
  ]);
}

export function is_what_happened_question(question = {}) {
  return label_includes(question, [
    'tell us what happened',
    'what happened',
    'describe what happened',
    'what has happened',
    'incident description',
    'request description',
  ]);
}

export function submitter_name_from_submitted_records(application_forms = []) {
  return normalize_text(
    application_forms
      .flatMap(submitted_answer_records)
      .filter(is_submitter_question)
      .map(answer_value)
      .find(Boolean)
  );
}

export function what_happened_answer_from_submitted_records(application_forms = []) {
  return normalize_text(
    application_forms
      .flatMap(submitted_answer_records)
      .filter(is_what_happened_question)
      .map(answer_value)
      .find(Boolean)
  );
}

export function what_happened_answer_from_application_forms(application_forms = []) {
  return what_happened_answer_from_submitted_records(application_forms) || normalize_text(
    application_forms
      .flatMap(form_questions)
      .filter(is_what_happened_question)
      .map(answer_value)
      .find(Boolean)
  );
}
